import { Box, IconLink, Text } from 'braid-design-system';
import type { GraphQLType } from 'graphql';
import { type ReactNode, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router';

import { Strikethrough } from '#src/pages/schema/components/Strikethrough.tsx';

import { NameLabel } from '../NameLabel/NameLabel.tsx';

import * as styles from './NameTextLink.css';
import { scrollToHonouringScrollPaddingTop } from '#src/utils/scrollToHonouringScrollPaddingTop.ts';

export const NameTextLink = ({
  id,
  name,
  type,
  deprecated,
  required,
  highlight,
  to,
  children,
}: {
  id: string;
  name: string;
  type?: GraphQLType;
  deprecated: boolean;
  required?: boolean;
  highlight?: boolean;
  to: string;
  children?: ReactNode;
}) => {
  const { search } = useLocation();
  const [toPath, toHash] = to.split('#');
  const toWithSearch = `${toPath}${search}${toHash ? `#${toHash}` : ''}`;

  useLayoutEffect(() => {
    if (highlight) {
      const timeoutId = setTimeout(() => {
        const idTarget = document.querySelector(`[data-id="${id}"]`);
        const target = idTarget?.closest('tr');

        if (target) {
          scrollToHonouringScrollPaddingTop(target);
        }
      }, 1);

      return () => clearTimeout(timeoutId);
    }
  }, [id, highlight]);

  return (
    <Text
      tone={deprecated ? 'secondary' : undefined}
      weight="strong"
      data={{ id }}
    >
      <Box component="pre" className={styles.headAnchorLink}>
        <Strikethrough strike={deprecated}>
          <NameLabel
            inline
            required={required}
            type={type}
            highlight={highlight}
          >
            {name}
          </NameLabel>
        </Strikethrough>{' '}
        <Box
          component="span"
          className={
            highlight
              ? styles.deepAnchorLink.highlight
              : styles.deepAnchorLink.hidden
          }
        >
          <Link to={toWithSearch} preventScrollReset>
            <Box
              component="span"
              className={
                highlight ? styles.linkIcon.highlight : styles.linkIcon.hidden
              }
            >
              <IconLink />
            </Box>
          </Link>
        </Box>
        {children}
      </Box>
    </Text>
  );
};
